/* =========================================================
   LC Bar PDP — S1 Hero JS (Batch 6A)
   ==================================
   Fork of the powder hero engine (section-lc-powder-pdp-hero.js) with
   three bar-specific additions:
     1. PER-BAR pricing — the pack card per-line shows price / bar
        (leading integer of the option value × BARS_PER_BOX from
        data-bars-per-box; "Single Box" → 1 box → 10 bars).
     2. FIBER SWAP — fiber varies per flavor: the Fiber stat chip value
        updates from the [data-pdp-fiber] map, and the flavor line under
        the H1 appends the flavor's claim ("Blueberry · Good Source of
        Fiber"). Unmapped flavor → chip untouched, claim omitted.
     3. ACCENT SWAP — each flavor's wrapper color: --pdp-field on the
        section is set from the [data-pdp-accents] map (CSS var token
        names). Panel top bar + active pill follow automatically.
   Batch 6F added one more, driven by the Variety Pack flavor:
     4. STAT LABEL SWAP — a flavor may carry a `statlabel` (table field 7),
        which replaces the Fiber chip's LABEL as well as its value. The
        authored label is captured on init and restored for every flavor
        that leaves the field empty, so this is reversible, not one-way.
   Everything else — name-matched pickers with positional fallback,
   variant resolution, save-%, ATC, zero-flag image preload-swap,
   zoom modal — is the powder engine unchanged.
   ========================================================= */

(function () {
  'use strict';

  var root = document.querySelector('[data-pdp-hero]');
  var dataEl = document.querySelector('[data-pdp-variants]');
  if (!root || !dataEl) return;

  var variants;
  try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
  if (!variants || !variants.length) return;

  var section = root.closest('.lc-bar-pdp-hero');
  var barsPerBox = parseInt(root.getAttribute('data-bars-per-box'), 10) || 10;
  var perBarRound = root.getAttribute('data-per-bar-round') === 'true';

  // ---- THE FLAVOR TABLE (one JSON): {default_img, flavors: {name: {sub, fiber, claim, accent, img}}}
  var flavorTable = { flavors: {} };
  var ftEl = document.querySelector('[data-pdp-flavors]');
  if (ftEl) { try { flavorTable = JSON.parse(ftEl.textContent); } catch (e) { flavorTable = { flavors: {} }; } }
  var flavors = flavorTable.flavors || {};
  var panelImg = document.querySelector('.lc-pdp-hero__media-panel img');
  var zoomImg = document.querySelector('[data-zoom] img');
  var imgVerified = {};
  var fiberStat = document.querySelector('[data-fiber-stat]');
  var subEl = document.querySelector('[data-flavor-sub]');

  // 6F — the Fiber chip's LABEL, and the label the Liquid authored. Captured
  // once so a statlabel swap is reversible: flavors without field 7 restore
  // this exact string rather than a hard-coded "Fiber".
  var fiberLabel = fiberStat
    ? fiberStat.parentNode.querySelector('.lc-pdp-hero__stat-label') : null;
  var fiberLabelText = fiberLabel ? fiberLabel.textContent : '';

  function swapImage(flavor) {
    if (!panelImg) return;  // placeholder state — no img until IMG_PRESENT flips
    var f = flavors[flavor];
    var url = f && f.img;
    if (!url || url === panelImg.getAttribute('src')) return;
    if (imgVerified[url]) { applyImage(url); return; }
    var probe = new Image();
    probe.onload = function () {
      imgVerified[url] = true;
      if (state.flavor === flavor) applyImage(url);
    };
    probe.src = url;
  }
  function applyImage(url) {
    panelImg.src = url;
    if (zoomImg) zoomImg.src = url;
  }

  function swapAccent(flavor) {
    if (!section) return;
    var f = flavors[flavor];
    if (f && f.accent) section.style.setProperty('--pdp-field', 'var(' + f.accent + ')');
  }

  function swapFiber(flavor) {
    var f = flavors[flavor];
    if (f && fiberStat && f.fiber) fiberStat.textContent = f.fiber;
    // 6F — label follows field 7 when set, otherwise back to the authored one.
    if (fiberLabel) {
      fiberLabel.textContent = (f && f.statlabel) ? f.statlabel : fiberLabelText;
    }
    return f && f.claim ? f.claim : '';
  }

  function swapSub(flavor) {
    var f = flavors[flavor];
    // innerHTML: subs are our own authored copy and may carry entities (&mdash;)
    if (f && subEl && f.sub) subEl.innerHTML = f.sub;
  }

  // ---- pickers (fallback already resolved in Liquid; JS reads what rendered)
  var flavorPicker = root.querySelector('[data-picker="flavor"]');
  var bundlePicker = root.querySelector('[data-picker="bundle"]');
  if (!flavorPicker || !bundlePicker) return;
  var flavorKey = flavorPicker.getAttribute('data-opt-pos') === '2' ? 'o2' : 'o1';
  var bundleKey = flavorKey === 'o1' ? 'o2' : 'o1';

  var flavorPills = Array.prototype.slice.call(flavorPicker.querySelectorAll('.lc-pdp-hero__pill'));
  var packCards = Array.prototype.slice.call(bundlePicker.querySelectorAll('.lc-pdp-hero__pack'));
  var flavorLine = document.querySelector('[data-flavor-line]');
  var variantInput = root.querySelector('[data-variant-id]');
  var atcBtn = root.querySelector('[data-atc]');
  var atcPrice = root.querySelector('[data-atc-price]');
  var atcLabel = root.querySelector('[data-atc-label]');
  var atcText = atcLabel ? atcLabel.textContent : 'Add to Cart';

  // 6F — adopt whichever card Liquid pressed: it already knows which tiers
  // exist for the server-selected flavor, so a variety deep-link doesn't
  // start life pointing at a 3 Box Pack that has no variant. Positional
  // fallback kept for the no-pressed-card case.
  var pressedCard = null;
  for (var pc = 0; pc < packCards.length; pc++) {
    if (packCards[pc].getAttribute('aria-pressed') === 'true') {
      pressedCard = packCards[pc];
      break;
    }
  }

  var state = {
    flavor: flavorPills.length ? flavorPills[0].getAttribute('data-value') : null,
    bundle: pressedCard
      ? pressedCard.getAttribute('data-value')
      : (packCards.length > 1 ? packCards[1].getAttribute('data-value') : (packCards.length ? packCards[0].getAttribute('data-value') : null))
  };
  var userInteracted = false; // URL sync only after a real selection, not initial paint

  // On load, adopt the URL's ?variant= if present — refresh + shared links
  // land on the flavor/pack they left on (05E port; the shared nutrition
  // section reads the same param server-side, so NFP matches on reload).
  (function adoptUrlVariant() {
    var m = window.location.search.match(/[?&]variant=(\d+)/);
    if (!m) return;
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === m[1]) {
        state.flavor = variants[i][flavorKey];
        state.bundle = variants[i][bundleKey];
        return;
      }
    }
  })();

  function money(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    });
  }

  // Per-bar display: exact cents, or whole-dollar when PER_BAR_ROUND is on.
  function moneyPerBar(cents) {
    if (perBarRound) return '$' + Math.round(cents / 100);
    return money(cents);
  }

  function packQty(value) {
    var m = String(value).match(/^(\d+)/);
    return m ? parseInt(m[1], 10) : 1;
  }

  function findVariant(flavor, bundle) {
    for (var i = 0; i < variants.length; i++) {
      if (variants[i][flavorKey] === flavor && variants[i][bundleKey] === bundle) {
        return variants[i];
      }
    }
    return null;
  }

  // 6F — reconcile pack against flavor: if the selected tier has no variant
  // for the new flavor, fall back to the FIRST available one (the cheapest),
  // never a costlier one. Every flavor currently carries all three tiers, so
  // this is a no-op today; it exists so a future single-tier flavor can't
  // strand the buy button. Called at the top of repaint.
  function ensureBundle() {
    if (findVariant(state.flavor, state.bundle)) return;
    for (var i = 0; i < packCards.length; i++) {
      var value = packCards[i].getAttribute('data-value');
      if (findVariant(state.flavor, value)) {
        state.bundle = value;
        return;
      }
    }
  }

  function repaint() {
    ensureBundle();

    var singleCard = packCards.length ? packCards[0] : null;
    var singleVariant = singleCard
      ? findVariant(state.flavor, singleCard.getAttribute('data-value')) : null;
    var singlePrice = singleVariant ? singleVariant.price : null;

    packCards.forEach(function (card) {
      var value = card.getAttribute('data-value');
      var boxes = packQty(value);
      var bars = boxes * barsPerBox;
      var v = findVariant(state.flavor, value);
      var priceEl = card.querySelector('[data-pack-price]');
      var perEl = card.querySelector('[data-pack-per]');
      var saveEl = card.querySelector('[data-pack-save]');

      card.setAttribute('aria-pressed', value === state.bundle ? 'true' : 'false');
      card.disabled = !v || v.available === false;

      if (!v) {
        priceEl.textContent = '\u2014';
        perEl.textContent = '';
        saveEl.hidden = true;
        return;
      }
      priceEl.textContent = money(v.price);
      perEl.textContent = moneyPerBar(Math.round(v.price / bars)) + ' / bar';

      if (boxes > 1 && singlePrice) {
        var full = singlePrice * boxes;
        var pct = Math.round(((full - v.price) / full) * 100);
        if (pct > 0) {
          saveEl.textContent = 'Save ' + pct + '%';
          saveEl.hidden = false;
        } else {
          saveEl.hidden = true;
        }
      } else {
        saveEl.hidden = true;
      }
    });

    flavorPills.forEach(function (pill) {
      pill.setAttribute('aria-pressed',
        pill.getAttribute('data-value') === state.flavor ? 'true' : 'false');
    });

    var claim = state.flavor ? swapFiber(state.flavor) : '';
    if (flavorLine) {
      flavorLine.textContent = state.flavor
        ? (claim ? state.flavor + ' \u00B7 ' + claim : state.flavor)
        : '\u00A0';
    }
    if (state.flavor) {
      swapAccent(state.flavor);
      swapImage(state.flavor);
      swapSub(state.flavor);
    }

    var selected = findVariant(state.flavor, state.bundle);
    if (selected && selected.available !== false) {
      variantInput.value = selected.id;
      if (userInteracted && window.history && window.history.replaceState) {
        try {
          var u = new URL(window.location.href);
          u.searchParams.set('variant', selected.id);
          window.history.replaceState({}, '', u.pathname + u.search);
        } catch (e) { /* URL API unavailable — skip, cosmetic only */ }
      }
      atcPrice.textContent = '\u00B7 ' + money(selected.price);
      if (atcLabel) atcLabel.textContent = atcText;
      atcBtn.disabled = false;
    } else {
      atcPrice.textContent = '';
      if (atcLabel) atcLabel.textContent = 'Temporarily Sold Out';
      atcBtn.disabled = true;
    }
  }

  flavorPills.forEach(function (pill) {
    pill.addEventListener('click', function () {
      userInteracted = true;
      state.flavor = pill.getAttribute('data-value');
      repaint();
    });
  });

  packCards.forEach(function (card) {
    card.addEventListener('click', function () {
      userInteracted = true;
      state.bundle = card.getAttribute('data-value');
      repaint();
    });
  });

  repaint();
})();

/* ---------- Zoom modal (only present when IMG_PRESENT) ---------- */
(function () {
  'use strict';

  var trigger = document.querySelector('[data-zoom-trigger]');
  var modal = document.querySelector('[data-zoom]');
  if (!trigger || !modal) return;
  var closeBtn = modal.querySelector('[data-zoom-close]');

  function open() {
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    if (closeBtn) closeBtn.focus();
  }
  function close() {
    modal.hidden = true;
    document.body.style.overflow = '';
    trigger.focus();
  }

  trigger.addEventListener('click', open);
  if (closeBtn) closeBtn.addEventListener('click', function (e) { e.stopPropagation(); close(); });
  modal.addEventListener('click', close);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !modal.hidden) close();
  });
})();
